import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from "react";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import "./qr-code.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import { qrSchemas, getQrFieldLabel } from "../utils/qrSchemas";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { clearAuthSession, hasValidPlatformAccess } from "../utils/auth";
import html2canvas from "html2canvas";
import Draggable from "react-draggable";

const QR_PAGE_SIZE = 5;

const BASE_URL_OPTIONS = [
  { key: "whatsapp", label: "WhatsApp", env: "REACT_APP_BASEURL_WHATSAPP" },
  { key: "website", label: "Website", env: "REACT_APP_PROD_WEBSITE_URL" },
  { key: "facebook", label: "Facebook", env: "REACT_APP_BASEURL_FACEBOOK" },
  { key: "instagram", label: "Instagram", env: "REACT_APP_BASEURL_INSTAGRAM" },
  { key: "tiktok", label: "TikTok", env: "REACT_APP_BASEURL_TIKTOK" }
];

const QR_TYPES = [
  "storefront",
  "product",
  "service",
  "promo",
  "order",
  "trial",
  "event",
  "custom"
];

function normalizeBaseUrlKey(dbValue) {
  if (!dbValue) return "website";
  const v = String(dbValue).trim();
  if (BASE_URL_OPTIONS.some(o => o.key === v)) return v;

  for (const o of BASE_URL_OPTIONS) {
    const envUrl = process.env[o.env];
    if (envUrl && v.startsWith(envUrl)) return o.key;
  }
  return "website";
}

export default function QrAdmin() {
  const { t, i18n } = useTranslation();
  const auth = useAuthSession();
  const { token, clientId, isAdmin, language } = auth;
  const [selectedClient, setSelectedClient] = useState(null);
  const [targetClientId, setTargetClientId] = useState(clientId || "");

  const [qrCodes, setQrCodes] = useState([]);
  const [editingQr, setEditingQr] = useState(null);
  const [qrType, setQrType] = useState("promo");
  const [payloadText, setPayloadText] = useState("{}");
  const [qrValue, setQrValue] = useState("");
  const [qrSubTitle, setQrSubTitle] = useState("");
  const [qrObjective, setQrObjective] = useState("");
  const [customPayloadRows, setCustomPayloadRows] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [qrToDelete, setQrToDelete] = useState(null);
  const [deletingQr, setDeletingQr] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [baseUrlKey, setBaseUrlKey] = useState("website");
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [qrPage, setQrPage] = useState(0);
  const [blockedOverwrite, setBlockedOverwrite] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const currentLocale = useMemo(() => {
    const lang = (i18n.language || language || "en").toLowerCase();
    return lang.startsWith("es") ? "es-ES" : "en-US";
  }, [i18n.language, language]);

  // Flyer Editor State
  const [flyerQr, setFlyerQr] = useState(null);
  const [flyerTitle, setFlyerTitle] = useState(t("qrc.flyer.defaultTitle", "Free 14-Day Trial!"));
  const [flyerDesc, setFlyerDesc] = useState(t("qrc.flyer.defaultDesc", "Scan the code to get started"));
  const [flyerBgImage, setFlyerBgImage] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [flyerBrightness, setFlyerBrightness] = useState(100);
  const [flyerOpacity, setFlyerOpacity] = useState(100);
  const [selectedElement, setSelectedElement] = useState(null);
  const [textStyles, setTextStyles] = useState({
    headline: { color: "#0f172a", size: 28, align: "center", bold: true },
    description: { color: "#334155", size: 18, align: "center", bold: false }
  });
  const bgRef = useRef(null);
  const headlineRef = useRef(null);
  const qrRef = useRef(null);
  const descRef = useRef(null);
  const [imageScale, setImageScale] = useState(1);

  const updateStyle = (key, field, val) => {
    setTextStyles(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: val }
    }));
  };

  const handleClientSelect = useCallback(client => {
    setSelectedClient(client);
    setTargetClientId(client.client_id);
  }, []);

  const hasPlatformAccess = useMemo(() => {
    return hasValidPlatformAccess(selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) return;
    if (!hasPlatformAccess) {
      setValidationMessage(t("dashboard.subscriptionRequiredDesc"));
      window.location.href = "/dashboard?tab=onboarding";
    }
  }, [hasPlatformAccess, selectedClient, t]);

  useEffect(() => {
    if (!isAdmin && clientId) {
      setTargetClientId(clientId);
    }
  }, [isAdmin, clientId]);

  const isCreateMode = editingQr && editingQr.__mode === "create";
  const isEditMode = editingQr && editingQr.qr_id;

  const paginatedQrs = qrCodes.slice(
    qrPage * QR_PAGE_SIZE,
    (qrPage + 1) * QR_PAGE_SIZE
  );

  const hasNextQrPage = (qrPage + 1) * QR_PAGE_SIZE < qrCodes.length;
  const hasPrevQrPage = qrPage > 0;

  useEffect(() => {
    setQrPage(0);
  }, [qrCodes]);

  function startCreateQr() {
    if (!targetClientId) {
      setValidationMessage(t("commonLabel.selectClientFirst", "Please select a client first."));
      return;
    }
    setEditingQr({ __mode: "create" });
    const defaultType = "promo";
    const template = generateTemplateForType(defaultType);
    setQrType(defaultType);
    setPayloadText(JSON.stringify(template, null, 2));
    setQrValue("");
    setQrSubTitle("");
    setQrObjective("");
    setCustomPayloadRows([]);
    setIsActive(true);
    setExpiresAt("");
    setBaseUrlKey("whatsapp");
  }

  function cancelEditQr() {
    setEditingQr(null);
  }

  async function performSave() {
    let payloadObj = null;
    try {
      payloadObj = buildPayloadFromUi();
      const schema = qrSchemas[qrType];

      if (schema?.required?.length) {
        for (const field of schema.required) {
          if (
            !payloadObj ||
            payloadObj[field] === undefined ||
            payloadObj[field] === null ||
            String(payloadObj[field]).trim() === ""
          ) {
            setValidationMessage(t("qrc.missingRequiredField", { field }));
            return;
          }
        }
      }
    } catch (e) {
      setValidationMessage(t("qrc.jsonError", "Contents must be valid JSON."));
      return;
    }

    const body = {
      client_id: targetClientId,
      qr_type: qrType,
      title: (qrSubTitle || "").trim() || "QR",
      objective: (qrObjective || "").trim(),
      payload: payloadObj,
      is_active: !!isActive,
      expires_at: expiresAt ? expiresAt.replace("T", " ") + ":00" : null,
      qr_id: editingQr?.qr_id || null,
      base_url: baseUrlKey
    };

    const res = await fetch(`${BACKEND_BASE_URL}/api/qrcodes`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!res.ok) throw new Error("Failed to save QR");

    setEditingQr(null);
    await loadQrCodes(targetClientId);
  }

  async function saveQr() {
    if (!targetClientId) return;

    if (isEditMode) {
      const currentScanCount = editingQr?.scan_count || 0;
      if (currentScanCount > 0) {
        setBlockedOverwrite(true);
        return;
      }
      if (qrValue && qrValue.trim() !== "") {
        setConfirmOverwrite(true);
        return;
      }
    }
    await performSave();
  }

  const loadQrCodes = useCallback(
    async clientId => {
      const res = await fetch(`${BACKEND_BASE_URL}/api/qrcodes/search`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ client_id: clientId })
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuthSession();
          throw new Error("Invalid or expired token");
        }
        throw new Error("Failed to load QR codes");
      }

      const data = await res.json();
      setQrCodes(Array.isArray(data) ? data : []);
    },
    [token]
  );

  useEffect(() => {
    if (!token || !targetClientId) return;
    loadQrCodes(targetClientId).catch(e => setValidationMessage(e.message));
  }, [token, targetClientId, loadQrCodes]);

  function dbTsToInputValue(ts) {
    if (!ts) return "";
    const s = String(ts).replace("T", " ").replace("Z", "");
    const [datePart, timePart = "00:00:00"] = s.split(" ");
    const hhmm = timePart.slice(0, 5);
    return `${datePart}T${hhmm}`;
  }

  function formatDbTsNoTz(ts) {
    if (!ts) return "";
    const s = String(ts).replace("T", " ").replace("Z", "");
    const [d, t = "00:00:00"] = s.split(" ");
    const [Y, M, D] = d.split("-").map(Number);
    const [hh, mm] = t.split(":").map(Number);

    const dateObj = new Date(Y, M - 1, D, hh, mm);
    return dateObj.toLocaleString(currentLocale, {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  }

  function getQrTypeLabel(type) {
    return t(`qrc.types.${type}`, type);
  }

  function startEditQr(qr) {
    setEditingQr(qr);
    setQrType(qr.qr_type);

    const payload = qr.payload || {};
    const required = qrSchemas[qr.qr_type]?.required || [];

    setPayloadText(JSON.stringify(payload, null, 2));
    setQrValue(qr.qr_value || "");
    setQrSubTitle(qr.title || "");
    setQrObjective(qr.objective || "");
    setIsActive(qr.is_active);
    setExpiresAt(dbTsToInputValue(qr.expires_at));
    setBaseUrlKey(normalizeBaseUrlKey(qr.base_url));

    setCustomPayloadRows(
      Object.keys(payload)
        .filter(k => !required.includes(k))
        .map(k => ({ key: k, value: payload[k] ?? "" }))
    );
  }

  async function confirmDeleteQr() {
    if (!qrToDelete) return;
    setDeletingQr(true);

    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/qrcodes`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          qr_id: qrToDelete.qr_id,
          client_id: targetClientId
        })
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuthSession();
          throw new Error("Invalid or expired token");
        }
        throw new Error("Delete failed");
      }

      setQrCodes(prev => prev.filter(q => q.qr_id !== qrToDelete.qr_id));
      setQrToDelete(null);
    } finally {
      setDeletingQr(false);
    }
  }

  const handleAdd = async () => {
    try {
      if (!targetClientId) {
        setValidationMessage(t("commonLabel.selectClientFirst", "Please select a client first."));
        return;
      }

      let payloadObj = null;
      try {
        payloadObj = buildPayloadFromUi();
      } catch (e) {
        setValidationMessage(t("qrc.jsonError", "Contents must be valid JSON."));
        return;
      }

      const body = {
        client_id: targetClientId,
        qr_type: qrType,
        title: (qrSubTitle || "").trim() || "QR",
        objective: (qrObjective || "").trim(),
        payload: payloadObj,
        is_active: !!isActive,
        expires_at: expiresAt ? expiresAt.replace("T", " ") + ":00" : null,
        base_url: baseUrlKey
      };

      const res = await fetch(`${BACKEND_BASE_URL}/api/qrcodes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setValidationMessage(errData.error || "Failed to add QR");
        return;
      }

      await loadQrCodes(targetClientId);
      setEditingQr(null);
      setQrValue("");
      setPayloadText("{}");
      setQrSubTitle("");
      setExpiresAt("");
    } catch (err) {
      console.error("Add QR failed:", err);
      setValidationMessage(t("qrc.invalidPayload", "Invalid payload JSON"));
    }
  };

  function generateTemplateForType(type) {
    const schema = qrSchemas[type];
    if (!schema) return {};
    const template = {};
    schema.required.forEach(field => {
      template[field] = "";
    });
    return template;
  }

  function addCustomPayloadRow() {
    setCustomPayloadRows(prev => [...prev, { key: "", value: "" }]);
  }

  function updateCustomPayloadRow(index, field, value) {
    setCustomPayloadRows(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  }

  function deleteCustomPayloadRow(index) {
    setCustomPayloadRows(prev => prev.filter((_, i) => i !== index));
  }

  function buildPayloadFromUi() {
    const payload = parsePayloadObject();
    for (const row of customPayloadRows) {
      const key = String(row.key || "").trim();
      if (key) payload[key] = row.value;
    }
    return payload;
  }

  function parsePayloadObject() {
    try {
      return payloadText ? JSON.parse(payloadText) : {};
    } catch {
      return {};
    }
  }

  function setPayloadField(field, value) {
    const current = parsePayloadObject();
    setPayloadText(JSON.stringify({ ...current, [field]: value }, null, 2));
  }

  function getPayloadField(field) {
    const current = parsePayloadObject();
    return current[field] ?? "";
  }

  function getPayloadFieldsForType(type) {
    const schema = qrSchemas[type];
    if (!schema) return [];
    return (schema.required || []).map(field => ({ field, required: true }));
  }

  const downloadFlyer = async () => {
    const flyerElement = document.getElementById("flyer-preview-capture");
    if (!flyerElement) return;

    setIsExporting(true);
    try {
      const canvas = await html2canvas(flyerElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: null
      });

      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `Flyer-${flyerQr?.title || "Promo"}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Failed to generate flyer:", err);
      setValidationMessage(t("qrc.flyerError", "Error generating flyer."));
    } finally {
      setIsExporting(false);
    }
  };

  const handleBgImageUpload = e => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = event => setFlyerBgImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  function renderQrForm() {
    return (
      <>
        <div className="qrc-row">
          <label>{t("qrc.type")}</label>
          <select
            className="qrc-light-input"
            value={qrType}
            onChange={e => {
              const newType = e.target.value;
              setQrType(newType);
              const template = generateTemplateForType(newType);
              setPayloadText(JSON.stringify(template, null, 2));
            }}
          >
            {QR_TYPES.map(value => (
              <option key={value} value={value}>
                {t(`qrc.types.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="qrc-row">
          <label>{t("qrc.subTitle")}</label>
          <input
            className="qrc-light-input"
            value={qrSubTitle}
            onChange={e => setQrSubTitle(e.target.value)}
            placeholder={t("qrc.titlePlaceholder")}
          />
        </div>

        <div className="qrc-row">
          <label>{t("qrc.objective")}</label>
          <input
            className="qrc-light-input"
            value={qrObjective}
            onChange={e => setQrObjective(e.target.value)}
            placeholder={t("qrc.objectivePlaceholder")}
          />
        </div>

        <div className="qrc-row">
          <div className="qrc-contents-header">
            <label>{t("qrc.contents")}</label>
            <button
              type="button"
              className="btn-link"
              onClick={addCustomPayloadRow}
            >
              {t("qrc.addOption", "+ option")}
            </button>
          </div>

          <div className="qrc-payload-grid">
            {getPayloadFieldsForType(qrType).map(({ field, required }) => (
              <div className="qrc-payload-row" key={field}>
                <label className="qrc-payload-label">
                  {getQrFieldLabel(field, language)}
                  {required ? " *" : ""}
                </label>
                <input
                  className="qrc-light-input"
                  value={getPayloadField(field)}
                  onChange={e => setPayloadField(field, e.target.value)}
                  placeholder={t("qrc.requiredPlaceholder")}
                />
              </div>
            ))}
          </div>

          {customPayloadRows.map((row, index) => (
            <div className="qrc-custom-option-row" key={index}>
              <input
                className="qrc-light-input"
                value={row.key}
                onChange={e => updateCustomPayloadRow(index, "key", e.target.value)}
                placeholder={t("qrc.optionalKeyPlaceholder")}
              />
              <input
                className="qrc-light-input"
                value={row.value}
                onChange={e => updateCustomPayloadRow(index, "value", e.target.value)}
                placeholder={t("qrc.optionalValuePlaceholder")}
              />
              <button
                type="button"
                className="qrc-option-delete"
                onClick={() => deleteCustomPayloadRow(index)}
              >
                X
              </button>
            </div>
          ))}
        </div>

        <div className="qrc-row">
          <label>{t("qrc.baseUrl")}</label>
          <select
            className="qrc-light-input"
            value={baseUrlKey}
            onChange={e => setBaseUrlKey(e.target.value)}
          >
            {BASE_URL_OPTIONS.map(o => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="qrc-row">
          <label>{t("qrc.value")}</label>
          <input
            className="qrc-light-input"
            value={qrValue}
            readOnly
            placeholder={t("qrc.valuePlaceholder")}
          />
        </div>

        <div className="qrc-row">
          <label>{t("qrc.qrPreview")}</label>
          {!qrValue || qrValue.trim() === "" ? (
            <div className="qrc-no-preview">{t("qrc.noPreview")}</div>
          ) : (
            <QRCodeCanvas
              value={qrValue}
              size={160}
              bgColor="#ffffff"
              fgColor="#0f172a"
              level="M"
            />
          )}
        </div>

        <div className="qrc-row">
          <label>
            <input
              type="checkbox"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
            />{" "}
            {t("commonLabel.active")}
          </label>
        </div>

        <div className="qrc-row">
          <label>{t("qrc.expires")}</label>
          <input
            className="qrc-light-input"
            type="datetime-local"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
          />
        </div>

        <div className="view-actions">
          <button className="btn-mini" onClick={cancelEditQr}>
            {t("commonLabel.cancel")}
          </button>
          <button className="btn-mini primary" onClick={saveQr}>
            {t("commonLabel.save")}
          </button>
          <button className="btn-mini primary" onClick={handleAdd}>
            {t("commonLabel.add")}
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="view-page">
      <div className="view-card">
        <div className="view-header">
          <div>
            <h1 className="view-title">{t("qrc.title")}</h1>
            <p className="view-subtitle">{t("qrc.subtitle")}</p>
          </div>

          <div className="view-nav">
            <select
              className="view-dropdown"
              defaultValue=""
              onChange={e => {
                const route = e.target.value;
                if (route) window.location.href = route;
              }}
            >
              <option value="" disabled>{t("dropDown.qr-page")}</option>
              <option value="/">{t("dropDown.home-page")}</option>
              <option value="/support">{t("dropDown.support-page")}</option>
              <option value="/clients">{t("dropDown.clients-page")}</option>
              <option value="/dashboard">{t("dropDown.dashboard-page")}</option>
              <option value="/crm-lite">{t("dropDown.crm-lite-page")}</option>
              <option value="/scheduler">{t("dropDown.scheduler-page")}</option>
              <option value="/commerce">{t("dropDown.commerce-page")}</option>
              <option value="/dispatcher">{t("dropDown.dispatcher-page")}</option>
              <option value="/analytics">{t("dropDown.analytics-page")}</option>
              <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
            </select>
          </div>
        </div>

        <ClientSearch onSelect={handleClientSelect} />

        <div className="qrc-tools">
          <div className="qrc-tools-header">
            <div className="qrc-section-title">{t("qrc.createQr")}</div>
            <button className="btn-link" onClick={() => startCreateQr()}>
              {t("qrc.newQr")}
            </button>
          </div>

          {isCreateMode && (
            <div className="commonLabel.edit">
              <div className="qrc-edit-title">✏️ {t("qrc.createQr")}</div>
              {renderQrForm()}
            </div>
          )}

          <div className="qrc-list">
            {paginatedQrs.map(qr => {
              const isEditingThis = isEditMode && editingQr.qr_id === qr.qr_id;

              return (
                <React.Fragment key={qr.qr_id}>
                  {isEditingThis ? (
                    <div className="commonLabel.edit">
                      <div className="qrc-edit-title">✏️ {t("qrc.editQr")}</div>
                      {renderQrForm()}
                    </div>
                  ) : (
                    <div className="qr-row">
                      <div className="qrc-item qr-card" onClick={() => startEditQr(qr)}>
                        <div className="qrc-item-main">
                          <div className="qrc-item-title">
                            {getQrTypeLabel(qr.qr_type)} – {qr.title || "QR"}
                          </div>
                          <div className="qrc-item-meta">
                            📊 {t("qrc.scanned")}: {qr.scan_count || 0}
                          </div>
                          {qr.expires_at && (
                            <div className="qrc-item-meta">
                              ⏰ {t("qrc.expires")}: {formatDbTsNoTz(qr.expires_at)}
                            </div>
                          )}
                          <div className="qrc-item-meta">
                            {t("commonLabel.status")}:{" "}
                            {qr.is_active ? t("commonLabel.active") : t("commonLabel.inactive")}
                          </div>
                        </div>
                        <button
                          className="qr-delete-btn side"
                          onClick={e => {
                            e.stopPropagation();
                            setQrToDelete(qr);
                          }}
                          title={t("commonLabel.delete")}
                        >
                          ×
                        </button>
                        <button
                          className="btn-mini secondary"
                          style={{ marginTop: "10px" }}
                          onClick={e => {
                            e.stopPropagation();
                            setFlyerQr({ ...qr });
                          }}
                        >
                          🎨 {t("qrc.flyer.createFlyer", "Create Flyer")}
                        </button>
                      </div>

                      {qr.qr_value && qr.qr_value.trim() !== "" && (
                        <div className="qrc-qr-preview">
                          <QRCodeCanvas
                            value={qr.qr_value}
                            size={96}
                            bgColor="#ffffff"
                            fgColor="#0f172a"
                            level="M"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {qrCodes.length > QR_PAGE_SIZE && (
            <div className="common-pagination">
              {hasPrevQrPage && (
                <button className="btn-link" onClick={() => setQrPage(p => p - 1)}>
                  {t("commonLabel.previous")}
                </button>
              )}
              {hasNextQrPage && (
                <button className="btn-link" onClick={() => setQrPage(p => p + 1)}>
                  {t("commonLabel.next")}
                </button>
              )}
            </div>
          )}
        </div>

        {qrToDelete && (
          <div className="confirm-overlay" onClick={() => setQrToDelete(null)}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("qrc.confirmDeleteTitle")}</div>
              <div className="confirm-text">
                {t("qrc.confirmDeleteMessage")} <strong>{qrToDelete.title}</strong>?
              </div>
              <div className="confirm-actions">
                <button className="btn-mini" onClick={() => setQrToDelete(null)}>
                  {t("commonLabel.cancel")}
                </button>
                <button className="btn-mini primary" disabled={deletingQr} onClick={confirmDeleteQr}>
                  {t("commonLabel.delete")}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmOverwrite && (
          <div className="confirm-overlay" onClick={() => setConfirmOverwrite(false)}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("qrc.overwriteTitle")}</div>
              <div className="confirm-text">{t("qrc.overwriteMessage")}</div>
              <div className="confirm-actions">
                <button className="btn-mini" onClick={() => setConfirmOverwrite(false)}>
                  {t("commonLabel.cancel")}
                </button>
                <button
                  className="btn-mini primary"
                  onClick={async () => {
                    setConfirmOverwrite(false);
                    await performSave();
                  }}
                >
                  {t("qrc.overwrite")}
                </button>
              </div>
            </div>
          </div>
        )}

        {blockedOverwrite && (
          <div className="confirm-overlay" onClick={() => setBlockedOverwrite(false)}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("qrc.blockedTitle")}</div>
              <div className="confirm-text">
                {t("qrc.blockedMessage1")}
                <br /><br />
                {t("qrc.blockedMessage2")}
              </div>
              <div className="confirm-actions">
                <button className="btn-mini primary" onClick={() => setBlockedOverwrite(false)}>
                  {t("commonLabel.ok")}
                </button>
              </div>
            </div>
          </div>
        )}

        {validationMessage && (
          <div className="confirm-overlay" onClick={() => setValidationMessage("")}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("qrc.validationTitle")}</div>
              <div className="confirm-text">{validationMessage}</div>
              <div className="confirm-actions">
                <button
                  className="btn-mini primary"
                  onClick={() => {
                    if (!hasPlatformAccess && selectedClient) {
                      window.location.href = "/dashboard?tab=onboarding";
                    }
                    setValidationMessage("");
                  }}
                >
                  {t("commonLabel.ok")}
                </button>
              </div>
            </div>
          </div>
        )}

        {flyerQr && (
          <div className="confirm-overlay" onClick={() => setFlyerQr(null)}>
            <div className="confirm-modal flyer-modal-container" onClick={e => e.stopPropagation()}>
              <div className="flyer-preview-container">
                <div id="flyer-preview-capture" onClick={() => setSelectedElement(null)}>
                  {flyerBgImage && (
                    <Draggable nodeRef={bgRef} bounds="parent">
                      <div
                        ref={bgRef}
                        style={{
                          position: "absolute",
                          zIndex: 0,
                          width: `${Math.round(400 * imageScale)}px`,
                          height: "auto",
                          cursor: "move"
                        }}
                      >
                        <img
                          src={flyerBgImage}
                          className="flyer-bg-img"
                          alt="bg"
                          style={{
                            width: "100%",
                            height: "auto",
                            filter: `brightness(${flyerBrightness}%) opacity(${flyerOpacity}%)`
                          }}
                        />
                      </div>
                    </Draggable>
                  )}

                  <Draggable nodeRef={headlineRef} bounds="parent">
                    <h1
                      ref={headlineRef}
                      className={`flyer-element ${selectedElement === "headline" ? "selected" : ""}`}
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedElement("headline");
                      }}
                      style={{
                        fontSize: `${textStyles.headline.size}px`,
                        color: textStyles.headline.color,
                        textAlign: textStyles.headline.align,
                        fontWeight: textStyles.headline.bold ? "bold" : "normal",
                        width: "380px"
                      }}
                    >
                      {flyerTitle}
                    </h1>
                  </Draggable>

                  <Draggable nodeRef={qrRef} bounds="parent" defaultPosition={{ x: 120, y: 150 }}>
                    <div ref={qrRef} className="flyer-qr-wrapper" onClick={e => e.stopPropagation()}>
                      <QRCodeSVG
                        value={flyerQr?.qr_value || "https://mercadito.ai"}
                        size={140}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                  </Draggable>

                  <Draggable nodeRef={descRef} bounds="parent" defaultPosition={{ x: 20, y: 300 }}>
                    <div
                      ref={descRef}
                      className={`flyer-element ${selectedElement === "description" ? "selected" : ""}`}
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedElement("description");
                      }}
                      style={{
                        fontSize: `${textStyles.description.size}px`,
                        color: textStyles.description.color,
                        textAlign: textStyles.description.align,
                        fontWeight: textStyles.description.bold ? "bold" : "normal",
                        width: "350px"
                      }}
                    >
                      {flyerDesc}
                    </div>
                  </Draggable>
                </div>
              </div>

              <div className="flyer-controls">
                <h2 className="flyer-title">🎨 {t("qrc.flyer.designFlyer", "Design Flyer")}</h2>

                <div className="qrc-row">
                  <label>{t("qrc.flyer.headline", "Headline Text")}</label>
                  <input
                    className="qrc-light-input"
                    value={flyerTitle}
                    onChange={e => setFlyerTitle(e.target.value)}
                  />
                </div>

                <div className="qrc-row">
                  <label>{t("qrc.flyer.description", "Description Text")}</label>
                  <input
                    className="qrc-light-input"
                    value={flyerDesc}
                    onChange={e => setFlyerDesc(e.target.value)}
                  />
                </div>

                <div className="flyer-formatting-box">
                  <h4>
                    {selectedElement
                      ? `${t("qrc.flyer.formatting", "Formatting")}: ${selectedElement.toUpperCase()}`
                      : t("qrc.flyer.clickToEdit", "Click text to edit style")}
                  </h4>
                  {selectedElement && (
                    <>
                      <div className="qrc-row">
                        <label>{t("qrc.flyer.color", "Color")}</label>
                        <input
                          type="color"
                          value={textStyles[selectedElement].color}
                          onChange={e => updateStyle(selectedElement, "color", e.target.value)}
                          style={{ width: "100%", height: "30px" }}
                        />
                      </div>
                      <div className="qrc-row">
                        <label>{t("qrc.flyer.size", "Size")}: {textStyles[selectedElement].size}px</label>
                        <input
                          type="range"
                          min="12"
                          max="80"
                          value={textStyles[selectedElement].size}
                          onChange={e => updateStyle(selectedElement, "size", Number(e.target.value))}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div className="qrc-row" style={{ display: "flex", gap: "5px" }}>
                        <button
                          type="button"
                          className="btn-mini"
                          onClick={() => updateStyle(selectedElement, "align", "left")}
                        >
                          {t("qrc.flyer.alignLeft", "Left")}
                        </button>
                        <button
                          type="button"
                          className="btn-mini"
                          onClick={() => updateStyle(selectedElement, "align", "center")}
                        >
                          {t("qrc.flyer.alignCenter", "Center")}
                        </button>
                        <button
                          type="button"
                          className="btn-mini"
                          onClick={() => updateStyle(selectedElement, "align", "right")}
                        >
                          {t("qrc.flyer.alignRight", "Right")}
                        </button>
                        <button
                          type="button"
                          className="btn-mini"
                          onClick={() => updateStyle(selectedElement, "bold", !textStyles[selectedElement].bold)}
                        >
                          {textStyles[selectedElement].bold
                            ? t("qrc.flyer.normal", "Normal")
                            : t("qrc.flyer.bold", "Bold")}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="qrc-row">
                  <label>{t("qrc.flyer.bgImage", "Background Image")}</label>
                  <input type="file" accept="image/*" onChange={handleBgImageUpload} />
                  {flyerBgImage && (
                    <div style={{ marginTop: "10px" }}>
                      <label>{t("qrc.flyer.brightness", "Brightness")}: {flyerBrightness}%</label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        value={flyerBrightness}
                        onChange={e => setFlyerBrightness(e.target.value)}
                        style={{ width: "100%" }}
                      />
                      <label>{t("qrc.flyer.opacity", "Opacity")}: {flyerOpacity}%</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={flyerOpacity}
                        onChange={e => setFlyerOpacity(e.target.value)}
                        style={{ width: "100%" }}
                      />
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => {
                          setFlyerBgImage(null);
                          setFlyerBrightness(100);
                          setFlyerOpacity(100);
                          setImageScale(1);
                        }}
                        style={{ marginTop: "10px", color: "#dc2626" }}
                      >
                        {t("qrc.flyer.removeImage", "Remove Image")}
                      </button>
                    </div>
                  )}
                </div>

                <div className="confirm-actions" style={{ marginTop: "20px" }}>
                  <button type="button" className="btn-mini" onClick={() => setFlyerQr(null)}>
                    {t("commonLabel.cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn-mini primary"
                    onClick={downloadFlyer}
                    disabled={isExporting}
                  >
                    {isExporting ? t("qrc.flyer.exporting", "Exporting...") : `⬇️ ${t("qrc.flyer.downloadFlyer", "Download Flyer (PNG)")}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}